import { Box, Flex } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { QueueHelper } from 'classes/helpers/queue.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineAutoFireButton } from 'components/machine/buttons/auto-fire';
import { PLSidebarQueueButton } from 'components/sections/pitch-list/sidebar/queue-button';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { AimingContext } from 'contexts/aiming.context';
import { MachineButton } from 'contexts/firing/button';
import { FiringContext } from 'contexts/firing/context';
import { HittersContext } from 'contexts/hitters.context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { IgnoreAutoFireMode } from 'enums/firing';
import { ResetPlateMode } from 'enums/machine.enums';
import { ProgressDirection } from 'enums/training.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IVerboseComponent } from 'interfaces/i-components';
import { IFireData } from 'interfaces/i-firing';
import { ISendBP } from 'interfaces/i-pitch-aiming';
import { AUTO_ADVANCE_QUEUE_TYPES, QueueType } from 'interfaces/i-queue-mode';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { MIN_FREQUENCY } from 'lib_ts/enums/pitches.enums';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { IFireResponseMsg } from 'lib_ts/interfaces/machine-msg/i-fire-response';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';

const COMPONENT_NAME = 'PitchListSidebarControls';

export const PitchListSidebarControls = (
  props: IVerboseComponent & {
    onTrainFn?: TrainPitchesFn;
  }
) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const mode = usePitchListStore((s) => s.mode);
  const filteredPitches = usePitchListStore((s) => s.filteredPitches);
  const queueType = usePitchListStore((s) => s.queueType);
  const queuePitchIDs = usePitchListStore((s) => s.queuePitchIDs);
  const selectedPitch = usePitchListStore((s) => s.selectedPitch);

  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const { machine, resendLastTarget } = useContext(MachineContext);

  const { active: activeHitter } = useContext(HittersContext);
  const { setAndSendForBP } = useContext(AimingContext);
  const { checkedOrder } = useContext(CheckedContext);

  const { safeSetIgnoreAf } = useContext(FiringContext);

  const activeList = useListLibraryStore((s) => s.activeList);

  const latestResendLastTarget = useLatest(resendLastTarget);

  // pitches that can/should be queued up
  const qablePitches = useMemo(() => {
    const trained = filteredPitches.filter((p) => p._mx_tms?._mx_trained);

    // ordered by selection order (checkedOrder) so the queue reflects the order
    // in which the user checked the pitches rather than the list/sort order
    const trainedAndChecked = checkedOrder.flatMap((id) => {
      const pitch = trained.find((p) => p._id === id);
      return pitch ? [pitch] : [];
    });

    if (trainedAndChecked.length > 0) {
      return trainedAndChecked;
    }

    return trained;
  }, [filteredPitches, machine.last_hardware_changed, checkedOrder]);

  const latestQablePitches = useLatest(qablePitches);

  // change queue type => reset queue based on the mode
  useEffect(() => {
    verboseDebug(
      props,
      `update queue > ${queueType} > ${qablePitches.length} queue-able pitches`
    );

    switch (queueType) {
      case QueueType.RepeatOne:
      case QueueType.RepeatAll: {
        usePitchListStore
          .getState()
          .setQueuePitchIDs(qablePitches.map((p) => p._id));
        return;
      }

      case QueueType.ShuffleEach:
      default: {
        usePitchListStore.getState().setQueuePitchIDs([]);
        return;
      }
    }
  }, [queueType, qablePitches]);

  const changeQueuePitch = useCallback(
    async (dir: ProgressDirection) => {
      function getNextPitch() {
        switch (queueType) {
          case QueueType.RepeatOne:
          case QueueType.RepeatAll: {
            const index = queuePitchIDs.findIndex(
              (id) => id === selectedPitch?._id
            );

            const nextPitchID =
              queuePitchIDs[(index + dir) % queuePitchIDs.length];

            return filteredPitches.find((p) => p._id === nextPitchID);
          }

          case QueueType.ShuffleEach: {
            const nextPitchID = QueueHelper.getShufflePitchID(
              qablePitches.map((p) => ({
                _id: p._id,
                frequency: p.frequency ?? MIN_FREQUENCY,
              }))
            );

            return filteredPitches.find((p) => p._id === nextPitchID);
          }

          default: {
            // shouldn't trigger
            console.warn(`Encountered an unexpected queue type: ${queueType}`);
            return;
          }
        }
      }

      const nextPitch = getNextPitch();

      if (!nextPitch) {
        return;
      }

      safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);

      await setAndSendForBP({
        pitch: nextPitch,
        resetPlate: ResetPlateMode.PitchBackup,
        sendConfig: {
          skipFiringCheck: true,
          skipPreview: !machine.enable_pitch_preview,
          trigger: mode,
          list: activeList,
          hitter: activeHitter,
        },
      });

      verboseDebug(props, `setting selected pitch > ${nextPitch.name}`);
      usePitchListStore.getState().setSelectedPitch(nextPitch);

      safeSetIgnoreAf(IgnoreAutoFireMode.Disabled);
    },
    [
      machine.enable_pitch_preview,
      queueType, // how to pick next pitch
      queuePitchIDs, // for repeat all
      selectedPitch, // for moving +/- 1 from whatever is selected (if anything)
      qablePitches, // for shuffle
      setAndSendForBP, // for sending
      activeList, // for sending
      activeHitter, // for sending
      onDemand, // for getting aiming shots
      safeSetIgnoreAf,
      filteredPitches,
    ]
  );

  // if queue type requires, listen to fire responses to auto-send the next pitch
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      if (anyOpenDialogs) {
        return;
      }

      // pitches with a frequency assigned
      const hasValidShufflePitches =
        latestQablePitches.current.filter((p) => !!p.frequency).length > 0;

      const shouldResendTarget =
        // * If we're on repeat-one, just resend the last MS so that jitter state is applied correctly
        !AUTO_ADVANCE_QUEUE_TYPES.includes(queueType) ||
        // * Edge case handling - when we're on shuffle but no pitches have a frequency assigned, pitch lists act like repeat-one
        (queueType === QueueType.ShuffleEach && !hasValidShufflePitches);

      if (shouldResendTarget) {
        latestResendLastTarget.current(COMPONENT_NAME);
        return;
      }

      const data: IFireResponseMsg = event.detail;

      if (!data) {
        NotifyHelper.error({
          message_md: `Empty fire response payload. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
        return;
      }

      if (!data.status) {
        console.error('Failed to fire', data);

        const message = data.message ?? 'Failed to fire, reason unknown';

        NotifyHelper.error({
          message_md: message,
          inbox: true,
        });
        return;
      }

      changeQueuePitch(ProgressDirection.Next);
    };

    WebSocketHelper.on(WsMsgType.M2U_FireResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_FireResponse, callback);
    };
  }, [queueType, anyOpenDialogs, changeQueuePitch]);

  const fireData = useMemo<IFireData>(() => {
    const o: IFireData = {
      mode: 'manual',
      trigger: 'click',
      training: false,
      training_mode: undefined,
      hitter_id: activeHitter?._id,
      tags: mode,
    };

    return o;
  }, [mode, activeHitter?._id]);

  const sendData = useMemo<ISendBP>(() => {
    const o: ISendBP = {
      skipPreview: !machine.enable_pitch_preview,
      trigger: mode,
      list: activeList,
      hitter: activeHitter,
    };

    return o;
  }, [machine.enable_pitch_preview, mode, activeList, activeHitter]);

  return (
    <ErrorBoundary identifier="PitchListSidebarControls">
      <Flex gap="2" direction="column">
        <Box>
          <MachineButton
            identifier="PLSidebarMachineButton"
            className="btn-block text-titlecase"
            fireData={fireData}
            sendData={sendData}
            onHotkeyChangePitch={changeQueuePitch}
            onTrainFn={props.onTrainFn}
            isSidebarControl
            enableHotkeys
          />
        </Box>
        <Flex gap="2">
          <PLSidebarQueueButton />
          <Box
            flexGrow="1"
            // necessary for button text to truncate
            minWidth="0"
          >
            <MachineAutoFireButton
              className="btn-block"
              confirm={!userRoleFeatureFlags.isSuperAdmin}
            />
          </Box>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
